import type {
  AddMemberRequest,
  ApprovalRowResponse,
  BudgetResponse,
  CreateAdjustmentRequest,
  CreateMilestoneRequest,
  CreateProjectRequest,
  CreateProjectTaskRequest,
  GenerateInvoiceRequest,
  GenerateResult,
  PersonResponse,
  ProjectAccountResponse,
  PreviewResponse,
  ProjectDetailResponse,
  ProjectResponse,
  ProjectTaskResponse,
  ProjectTaskStatusResponse,
  SetRateCardRequest,
  TaskBoardHeader,
  TimesheetEntryResponse,
  UnbilledRowResponse,
  UpdateMilestoneRequest,
  UpdateProjectRequest,
  UpdateProjectTaskRequest,
  UpsertCellRequest,
  WeekResponse,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * The projects data layer (PART 13): thin TanStack Query wrappers over the
 * typed contract client — projects (PRJ-01), the task board on the ONE
 * engine (PRJ-02), timesheets (PRJ-03) and billing (PRJ-04).
 */

export const PROJECTS_KEY = ['projects'] as const;
export const BOARD_KEY = ['projects', 'board'] as const;
export const WEEK_KEY = ['projects', 'week'] as const;
export const APPROVALS_KEY = ['projects', 'approvals'] as const;
export const BILLING_KEY = ['projects', 'billing'] as const;

// ── PRJ-01 ──────────────────────────────────────────────────────────────────

export interface ProjectListFilters {
  status?: ProjectResponse['status'] | undefined;
  managerId?: string | undefined;
  q?: string | undefined;
}

/**
 * PRJ-01's list, one page at a time (NFR-42) — every filter applied by the
 * SERVER. BR-PRJ-01-02's four headline figures ride BESIDE the page, so they
 * describe every Planning/Active/On hold project whatever the table is
 * narrowed to.
 */
export function usePagedProjects(
  filters: ProjectListFilters,
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'list', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.projects.list({
        query: {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.managerId ? { managerId: filters.managerId } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'one', projectId],
    enabled: Boolean(projectId),
    queryFn: async (): Promise<ProjectDetailResponse> => {
      const response = await api.projects.get({ params: { projectId: projectId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function usePeople() {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'people'],
    queryFn: async (): Promise<PersonResponse[]> => {
      const response = await api.projects.people();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.people;
    },
  });
}

/**
 * The other picker (see the contract's note): a TASK is assigned to an
 * account, because it lands in that person's own My Tasks. usePeople() is
 * the staff list, for the manager and the team.
 */
export function useAccounts() {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'accounts'],
    queryFn: async (): Promise<ProjectAccountResponse[]> => {
      const response = await api.projects.accounts();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.accounts;
    },
  });
}

function useProjectsInvalidate() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
}

export function useCreateProject() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async (body: CreateProjectRequest): Promise<ProjectDetailResponse> => {
      const response = await api.projects.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateProject() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: UpdateProjectRequest;
    }): Promise<ProjectDetailResponse> => {
      const response = await api.projects.update({ params: { projectId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useAddMember() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async ({ projectId, body }: { projectId: string; body: AddMemberRequest }) => {
      const response = await api.projects.addMember({ params: { projectId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async ({ projectId, memberId }: { projectId: string; memberId: string }) => {
      const response = await api.projects.removeMember({ params: { projectId, memberId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useSetRateCard() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async ({ projectId, body }: { projectId: string; body: SetRateCardRequest }) => {
      const response = await api.projects.setRateCard({ params: { projectId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useAddMilestone() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: CreateMilestoneRequest;
    }) => {
      const response = await api.projects.addMilestone({ params: { projectId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateMilestone() {
  const invalidate = useProjectsInvalidate();
  return useMutation({
    mutationFn: async ({
      milestoneId,
      body,
    }: {
      milestoneId: string;
      body: UpdateMilestoneRequest;
    }) => {
      const response = await api.projects.updateMilestone({ params: { milestoneId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── PRJ-02 ──────────────────────────────────────────────────────────────────

export interface BoardResponse {
  statuses: ProjectTaskStatusResponse[];
  tasks: ProjectTaskResponse[];
  header: TaskBoardHeader;
}

export function useBoard(projectId: string | undefined) {
  return useQuery({
    queryKey: [...BOARD_KEY, projectId],
    enabled: Boolean(projectId),
    queryFn: async (): Promise<BoardResponse> => {
      const response = await api.projectTasks.board({ params: { projectId: projectId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useBoardInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
    void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
  };
}

export function useCreateProjectTask() {
  const invalidate = useBoardInvalidate();
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: CreateProjectTaskRequest;
    }): Promise<ProjectTaskResponse> => {
      const response = await api.projectTasks.create({ params: { projectId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateProjectTask() {
  const invalidate = useBoardInvalidate();
  return useMutation({
    mutationFn: async ({
      taskId,
      body,
    }: {
      taskId: string;
      body: UpdateProjectTaskRequest;
    }): Promise<ProjectTaskResponse> => {
      const response = await api.projectTasks.update({ params: { taskId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useSetTaskStatus() {
  const invalidate = useBoardInvalidate();
  return useMutation({
    mutationFn: async ({ taskId, statusId }: { taskId: string; statusId: string }) => {
      const response = await api.projectTasks.setStatus({ params: { taskId }, body: { statusId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useTickTask() {
  const invalidate = useBoardInvalidate();
  return useMutation({
    mutationFn: async ({ taskId, tick }: { taskId: string; tick: boolean }) => {
      const response = tick
        ? await api.projectTasks.tick({ params: { taskId }, body: {} })
        : await api.projectTasks.untick({ params: { taskId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useAddDependency() {
  const invalidate = useBoardInvalidate();
  return useMutation({
    mutationFn: async ({ taskId, blockedByTaskId }: { taskId: string; blockedByTaskId: string }) => {
      const response = await api.projectTasks.addDependency({
        params: { taskId },
        body: { blockedByTaskId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useRemoveDependency() {
  const invalidate = useBoardInvalidate();
  return useMutation({
    mutationFn: async ({ taskId, blockedByTaskId }: { taskId: string; blockedByTaskId: string }) => {
      const response = await api.projectTasks.removeDependency({
        params: { taskId, blockedByTaskId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── PRJ-03 ──────────────────────────────────────────────────────────────────

export function useWeek(weekStart: string, userId?: string) {
  return useQuery({
    queryKey: [...WEEK_KEY, weekStart, userId ?? 'me'],
    queryFn: async (): Promise<WeekResponse> => {
      const response = await api.timesheets.week({
        query: { weekStart, ...(userId ? { userId } : {}) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useWeekInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: WEEK_KEY });
    void queryClient.invalidateQueries({ queryKey: APPROVALS_KEY });
    void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
    void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
  };
}

export function useUpsertCell() {
  const invalidate = useWeekInvalidate();
  return useMutation({
    mutationFn: async (body: UpsertCellRequest): Promise<WeekResponse> => {
      const response = await api.timesheets.upsertCell({ body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useSubmitWeek() {
  const invalidate = useWeekInvalidate();
  return useMutation({
    mutationFn: async (weekStart: string): Promise<WeekResponse> => {
      const response = await api.timesheets.submitWeek({ body: { weekStart } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useApprovals(enabled: boolean) {
  return useQuery({
    queryKey: [...APPROVALS_KEY],
    enabled,
    queryFn: async (): Promise<ApprovalRowResponse[]> => {
      const response = await api.timesheets.approvals();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.approvals;
    },
  });
}

export function useApproveWeek() {
  const invalidate = useWeekInvalidate();
  return useMutation({
    mutationFn: async (weekId: string) => {
      const response = await api.timesheets.approveWeek({ params: { weekId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useRejectWeek() {
  const invalidate = useWeekInvalidate();
  return useMutation({
    mutationFn: async ({ weekId, comment }: { weekId: string; comment: string }) => {
      const response = await api.timesheets.rejectWeek({ params: { weekId }, body: { comment } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useCreateAdjustment() {
  const invalidate = useWeekInvalidate();
  return useMutation({
    mutationFn: async (body: CreateAdjustmentRequest): Promise<TimesheetEntryResponse> => {
      const response = await api.timesheets.createAdjustment({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── PRJ-04 ──────────────────────────────────────────────────────────────────

export function usePreview(projectId: string | undefined) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'preview', projectId],
    enabled: Boolean(projectId),
    queryFn: async (): Promise<PreviewResponse> => {
      const response = await api.projectBilling.preview({ params: { projectId: projectId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useBudget(projectId: string | undefined) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'budget', projectId],
    enabled: Boolean(projectId),
    queryFn: async (): Promise<BudgetResponse> => {
      const response = await api.projectBilling.budget({ params: { projectId: projectId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useUnbilledAll() {
  return useQuery({
    queryKey: [...BILLING_KEY, 'unbilled'],
    queryFn: async (): Promise<UnbilledRowResponse[]> => {
      const response = await api.projectBilling.unbilled();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.rows;
    },
  });
}

/**
 * PRJ-04's invoice history, one page at a time (NFR-42) — a long-running
 * project gains a batch every billing period and never loses one.
 */
export function useHistory(
  projectId: string | undefined,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...BILLING_KEY, 'history', projectId, page, pageSize],
    enabled: Boolean(projectId),
    queryFn: async () => {
      const response = await api.projectBilling.history({
        params: { projectId: projectId! },
        query: { page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

function useBillingInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
    void queryClient.invalidateQueries({ queryKey: WEEK_KEY });
  };
}

export function useGenerateInvoice() {
  const invalidate = useBillingInvalidate();
  return useMutation({
    mutationFn: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: GenerateInvoiceRequest;
    }): Promise<GenerateResult> => {
      const response = await api.projectBilling.generate({ params: { projectId }, body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useBillMilestone() {
  const invalidate = useBillingInvalidate();
  return useMutation({
    mutationFn: async (milestoneId: string): Promise<GenerateResult> => {
      const response = await api.projectBilling.billMilestone({ params: { milestoneId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useBillProgress() {
  const invalidate = useBillingInvalidate();
  return useMutation({
    mutationFn: async ({
      projectId,
      toPct,
    }: {
      projectId: string;
      toPct: string;
    }): Promise<GenerateResult> => {
      const response = await api.projectBilling.billProgress({
        params: { projectId },
        body: { toPct },
      });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}
